import * as React from "react";
import { useHistory } from "react-router-dom";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import UploadIcon from "mdi-material-ui/Upload";

import {
  List,
  TextField,
  FunctionField,
  ReferenceInput,
  SelectInput,
  Datagrid,
  Button,
  CreateButton,
  EditButton,
  FilterButton,
  ShowButton,
  Filter,
  AutoCompleteInput
} from "jazasoft";

import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { getDistinctValues, inputOptions, isEmpty, listFromCsv, toFixed } from "../../../utils/helpers";
import { useEntityPrivilege, useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity } from "../../../utils/types";
import useWarehouse from "../../../hooks/useWarehouse";

const useStyles = makeStyles(theme => ({
  button: {
    width: theme.spacing(14)
  }
}));

const Invoice = props => {
  const classes = useStyles();
  const { roles, hasAccess } = props;

  const { disableCreate, disableEdit } = useEntityPrivilege("GRN");

  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const history = useHistory();
  const { allWhList } = useWarehouse();
  const whChoices = [{ id: "all", name: "All Warehouses" }, ...allWhList.map(({ code, name }) => ({ id: code, name }))];

  return (
    <List
      searchKeys={["invoiceNo", "erpInvoiceNo", "fabricItemList.grn"]}
      actions={({ basePath, roles, hasAccess, resource }) => (
        <div>
          {hasPrivilege(roles, hasAccess, ResourceId.INVOICE, Scope.WRITE) && !disableCreate && (
            <CreateButton basePath={basePath} showLabel={false} />
          )}
          {hasPrivilege(roles, hasAccess, ResourceId.INVOICE, Scope.WRITE) && !disableCreate && (
            <Button showLabel={false} label={"Excel Upload"} onClick={() => history && history.push(`${basePath}/upload`)}>
              <UploadIcon />
            </Button>
          )}
          <FilterButton showLabel={false} resource={resource} />
        </div>
      )}
      filters={
        <Filter>
          <SelectInput label="resources.invoices.fields.warehouse" source="warehouses" choices={whChoices} {...inputOptions(12)} />
          <ReferenceInput
            label="resources.invoices.fields.supplierId"
            source="supplierId"
            reference="suppliers"
            allowEmpty={true}
            {...inputOptions(12)}>
            <AutoCompleteInput disablePortal />
          </ReferenceInput>
        </Filter>
      }
      parse={({ supplierId, warehouses }) => ({ "supplier.id": supplierId, warehouses })}
      {...props}>
      <Datagrid className={classes.datagrid}>
        {invoiceFields.includes("invoiceNo") && <TextField source="invoiceNo" />}
        {invoiceFields.includes("erpInvoiceNo") && <TextField source="erpInvoiceNo" />}
        {invoiceFields.includes("grn") && (
          <FunctionField
            source="grn"
            render={record =>
              isEmpty(record?.fabricItemList)
                ? ""
                : getDistinctValues(record.fabricItemList.map(item => item.grn).filter(e => !isEmpty(e))).join(", ")
            }
          />
        )}
        {invoiceFields.includes("invoiceQty") && (
          <FunctionField
            source="invoiceQty"
            render={record =>
              isEmpty(record?.fabricItemList) ? "" : toFixed(record.fabricItemList.reduce((acc, e) => acc + (e.invoiceQty || 0), 0))
            }
          />
        )}
        {invoiceFields.includes("supplier") && <FunctionField source="supplier" render={record => record?.supplier?.name} />}
        {allWhList?.length > 1 && (
          <FunctionField
            source="warehouses"
            render={record =>
              allWhList
                .filter(wh => listFromCsv(record?.warehouses).includes(wh.code))
                .map(u => u.name)
                .join(", ")
            }
          />
        )}
        <ShowButton cellClassName={classes.button} />
        {hasPrivilege(roles, hasAccess, ResourceId.INVOICE, Scope.UPDATE) && !disableEdit && <EditButton cellClassName={classes.button} />}
      </Datagrid>
    </List>
  );
};

export default Invoice;
